import classnames from 'classnames';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Container, Heading, useModal } from 'yoso';
import { useLoginSecurity } from '~/components/Auth/Login/Security/LoginSecurity.hooks';
import { DonBalance } from '~/components/Balance';
import { useIsMobile } from '~/components/Breakpoint/Breakpoint.hook';
import { useDonHistoryParams } from '~/components/Don/History/DonHistory.hooks';
import { DonHistoryList } from '~/components/Don/History/List';
import { DonHistoryRewardPopup } from '~/components/Don/History/Popup/DonHistoryRewardPopup';
import { QueryErrorBoundary } from '~/components/Error';
import { HistoryErrorFallback } from '~/components/Error/History/HistoryErrorFallback';
import { HeaderSticky } from '~/components/Header';
import { t } from '~/components/I18n';
import { TabNav } from '~/components/Nav/Tab';
import { Page } from '~/components/Page';
import styles from './DonHistoryPage.module.scss';
import historyStyles from './HistoryPage.module.scss';

export const DonHistoryPage = () => {
  const { open } = useModal();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { transactionType } = useDonHistoryParams();
  const isMobile = useIsMobile();

  useLoginSecurity({ useErrorBoundary: true });

  const { search } = useLocation();

  useEffect(() => {
    if (id) {
      history.replace({
        pathname: '/membership/don/history',
        search,
      });
      open(<DonHistoryRewardPopup id={Number(id)} />);
    }
  }, [history, id, open, search]);

  return (
    <Page className={historyStyles.root} data-event-page_name="DON_History">
      <div className={historyStyles.header}>
        <Container margin="auto" className={styles.balanceContainer}>
          <div className={historyStyles.balance}>
            <Heading level={4} className={historyStyles.balanceTitle}>
              {t('citizen_history_balance_desc')}
            </Heading>
            <DonBalance icon />
          </div>
        </Container>
      </div>
      <div className={historyStyles.body}>
        <Container margin="auto" className={historyStyles.container}>
          <HeaderSticky
            className={historyStyles.headerSticky}
            rootMargin={isMobile ? '-93px 0px' : '-113px 0px'}
            initialInView={true}>
            <TabNav className={historyStyles.navTabs}>
              <li>
                <Link
                  to={{ search: '?transactionType=ALL', state: { scroll: 'restore' } }}
                  className={classnames({ [historyStyles.active]: transactionType === 'ALL' })}
                  replace>
                  {t('citizen_don_all_history')}
                </Link>
              </li>
              <li>
                <Link
                  to={{ search: '?transactionType=CHARGE', state: { scroll: 'restore' } }}
                  className={classnames({ [historyStyles.active]: transactionType === 'CHARGE' })}
                  replace>
                  {t('citizen_don_deposit_history')}
                </Link>
              </li>
              <li>
                <Link
                  to={{ search: '?transactionType=USE', state: { scroll: 'restore' } }}
                  className={classnames({ [historyStyles.active]: transactionType === 'USE' })}
                  replace>
                  {t('citizen_don_usage_history')}
                </Link>
              </li>
            </TabNav>
          </HeaderSticky>
          <QueryErrorBoundary
            FallbackComponent={HistoryErrorFallback}
            resetKeys={[transactionType]}>
            <DonHistoryList className={historyStyles.list} transactionType={transactionType} />
          </QueryErrorBoundary>
        </Container>
      </div>
    </Page>
  );
};
